'use client'

import { useMemo, useId, useLayoutEffect, useRef, useState } from 'react'

export type LineSeriesPoint = { x: number; y: number; label?: string }

type Props = {
  points: LineSeriesPoint[]
  /** CSS color for stroke */
  stroke?: string
  /** Viewbox height; width 100% */
  height?: number
  className?: string
  strokeWidth?: number
  /** Fill area under the line (stroke color at low opacity) */
  areaFill?: boolean
  areaOpacity?: number
  /** Subtle horizontal grid lines */
  showGrid?: boolean
  /** Animate stroke draw-in on mount */
  animate?: boolean
}

function buildPaths(
  points: LineSeriesPoint[],
  insetTop: number,
  insetBottom: number
): { lineD: string; areaD: string } | null {
  if (points.length < 2) return null
  const ys = points.map((p) => p.y)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const pad = maxY === minY ? 1 : (maxY - minY) * 0.08
  const y0 = minY - pad
  const y1 = maxY + pad
  const span = 100 - insetTop - insetBottom
  const n = points.length - 1

  let firstX = 0
  let lastX = 100
  const parts: string[] = []
  points.forEach((p, i) => {
    const px = (i / n) * 100
    const py = y1 === y0 ? 50 : insetTop + span * (1 - (p.y - y0) / (y1 - y0))
    if (i === 0) {
      firstX = px
      parts.push(`M ${px.toFixed(2)} ${py.toFixed(2)}`)
    } else {
      parts.push(`L ${px.toFixed(2)} ${py.toFixed(2)}`)
    }
    if (i === n) {
      lastX = px
    }
  })
  const lineD = parts.join(' ')
  const areaD = `${lineD} L ${lastX.toFixed(2)} 100 L ${firstX.toFixed(2)} 100 Z`
  return { lineD, areaD }
}

/** Lightweight SVG line chart — no external deps, CSP-safe. */
export function SigmaMiniLineChart({
  points,
  stroke = 'var(--p-color-border-emphasis, #8a8a8a)',
  height = 120,
  className,
  strokeWidth = 1.25,
  areaFill = false,
  areaOpacity = 0.08,
  showGrid = false,
  animate = false,
}: Props) {
  const uid = useId().replace(/:/g, '')
  const pathRef = useRef<SVGPathElement | null>(null)
  const [dash, setDash] = useState<{ len: number; offset: number } | null>(null)

  const insetTop = 4
  const insetBottom = 2

  const built = useMemo(
    () => buildPaths(points, insetTop, insetBottom),
    [points]
  )

  useLayoutEffect(() => {
    if (!animate || !built || !pathRef.current) {
      setDash(null)
      return
    }
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDash(null)
      return
    }
    const len = pathRef.current.getTotalLength()
    setDash({ len, offset: len })
    const id = requestAnimationFrame(() => {
      setDash({ len, offset: 0 })
    })
    return () => cancelAnimationFrame(id)
  }, [animate, built, points])

  if (!built) {
    return (
      <div className={className} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--p-color-text-subdued)' }}>
          Not enough daily data for a trend in this range.
        </span>
      </div>
    )
  }

  const { lineD, areaD } = built
  const gridStroke = 'var(--sigma-border, #e5e7eb)'
  const transition = animate ? 'stroke-dashoffset 0.65s ease-out' : undefined

  return (
    <svg
      className={className}
      width="100%"
      height={height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label="Trend chart"
    >
      <defs>
        <clipPath id={`${uid}-plot`}>
          <rect x="0" y="0" width="100" height="100" />
        </clipPath>
      </defs>
      {showGrid ? (
        <g clipPath={`url(#${uid}-plot)`} opacity={0.35}>
          {[25, 50, 75].map((gy) => (
            <line
              key={gy}
              x1="0"
              y1={gy}
              x2="100"
              y2={gy}
              stroke={gridStroke}
              strokeWidth={0.35}
              vectorEffect="non-scaling-stroke"
              strokeDasharray="1.2 2"
            />
          ))}
        </g>
      ) : null}
      {areaFill ? (
        <path
          d={areaD}
          fill={stroke}
          fillOpacity={areaOpacity}
          stroke="none"
          clipPath={`url(#${uid}-plot)`}
        />
      ) : null}
      <path
        ref={pathRef}
        d={lineD}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dash ? `${dash.len} ${dash.len}` : undefined}
        strokeDashoffset={dash?.offset}
        style={{ transition }}
      />
    </svg>
  )
}
